import { config } from "shared/data";

import {
  AB_WIDGET_API_TIMEOUT_MS,
  AB_WIDGET_FETCH_API_ENDPOINT,
  AB_WIDGET_FETCH_LOG_PREFIX,
} from "./constants";
import type { AbWidgetApiResponse, AbWidgetEntityIds } from "./types";

type ApiRequestParameters = {
  excludeArticleIds?: string[];
  reqRegion: string;
  userId?: string;
  visitorId: string;
};

const getRecommendParameters = () => {
  if (config.general.env === "production" || config.general.env === "preprod") {
    return;
  }

  const now = new Date();
  const endDate = new Date(now);
  endDate.setDate(now.getDate() - 8);
  const startDate = new Date(endDate);
  startDate.setDate(endDate.getDate() - 6);

  return {
    recall: {
      endDate: endDate.toISOString().split("T")[0],
      startDate: startDate.toISOString().split("T")[0],
    },
  };
};

export const fetchAbWidgetRecommendations = async (
  parameters: ApiRequestParameters,
): Promise<AbWidgetApiResponse> => {
  const recommendParameters = getRecommendParameters();
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), AB_WIDGET_API_TIMEOUT_MS);

  try {
    const response = await fetch(AB_WIDGET_FETCH_API_ENDPOINT, {
      body: JSON.stringify({
        ...(recommendParameters && { recommendParams: recommendParameters }),
        excludeArticleIds: parameters.excludeArticleIds,
        reqRegion: parameters.reqRegion,
        userId: parameters.userId,
        visitorId: parameters.visitorId,
      }),
      headers: { apikey: config.abWidgetApi.apiKey, "Content-Type": "application/json" },
      method: "POST",
      signal: controller.signal,
    });

    // TODO: Add proper error handling — for now throw on non-200
    if (!response.ok) {
      console.error(`${AB_WIDGET_FETCH_LOG_PREFIX} API error: ${response.status}`);
      throw new Error(`AB widget API error: ${response.status}`);
    }

    return response.json() as Promise<AbWidgetApiResponse>;
  } finally {
    clearTimeout(timeoutId);
  }
};

/**
 * Map API response widgets to named entityIds by widget type key.
 * Keys: feature_package_1 → Series, feature_package_2 → Open Question, geo → CFT, spotlight → Spotlight
 */
export const massageApiResponse = (response: AbWidgetApiResponse): AbWidgetEntityIds => {
  const widgets = response.data.recommendationArea.widgets;

  const widgetMap = new Map<string, string[]>();
  const widgetTopicEntityIdMap = new Map<string, string>();
  const widgetTopicNameMap = new Map<string, string>();

  for (const widget of widgets) {
    const entityIds = (widget.items ?? [])
      .toSorted((a, b) => a.slotId - b.slotId)
      .map(item => item.article.articleId);
    widgetMap.set(widget.type, entityIds);
    if (widget.topic?.id) {
      widgetTopicEntityIdMap.set(widget.type, widget.topic.id);
    }
    if (widget.topic?.name) {
      widgetTopicNameMap.set(widget.type, widget.topic.name);
    }
  }

  const result: AbWidgetEntityIds = {
    cft: widgetMap.get("geo") ?? [],
    cftTopicEntityId: widgetTopicEntityIdMap.get("geo"),
    cftTopicName: widgetTopicNameMap.get("geo"),
    openQuestion: widgetMap.get("feature_package_2") ?? [],
    openQuestionTopicEntityId: widgetTopicEntityIdMap.get("feature_package_2"),
    openQuestionTopicName: widgetTopicNameMap.get("feature_package_2"),
    recommendationId: response.data.requestId,
    series: (widgetMap.get("feature_package_1") ?? []).slice(0, 3),
    seriesRaw: widgetMap.get("feature_package_1") ?? [],
    seriesTopicEntityId: widgetTopicEntityIdMap.get("feature_package_1"),
    seriesTopicName: widgetTopicNameMap.get("feature_package_1"),
    spotlight: widgetMap.get("spotlight") ?? [],
  };

  return result;
};

export const getColdStart = (response: AbWidgetApiResponse): boolean =>
  response.data.reader.coldStart;
